import * as array from 'd3-array'
import * as hierarchy from 'd3-hierarchy'
import * as d3 from 'd3-selection'
import { EPSILON } from 'chart-constants'
import hierarchyDrilldown from './animations/hierarchy-drilldown-animation'
import hierarchyDrillup from './animations/hierarchy-drillup-animation'
import depthChange from './animations/depth-change-animation' // Must be after the drilldown/drillup animations
import basic from './animations/basic-animation'
import back from 'back-container'

export const animations = [
  hierarchyDrilldown,
  hierarchyDrillup,
  depthChange,
  basic
]

const outerPadding = 20

/*
 * Builds the tree hierarchy for the given breakdown and points.
 */
const buildHierarchy = (breakdown, serie) => {
  const lookup = {}
      , children = breakdown.range.map(point => Object.assign({}, point))

  // Build up a lookup table in case they aren't in order
  children.forEach(node => {
    lookup[node.id] = node
  })

  let root = {
    id: serie.id,
    title: serie.title,
    children: [],
    _point: null,
    drilldown: false,
    level: serie.level,
    _range: {
      title: serie.title,
      id: serie.id,
      parentId: undefined
    }
  }

  // Add the root (serie) to the lookup
  lookup[serie.id] = root

  // Compute children.
  children.forEach(node => {
    // Inject
    let point = serie.points.find(p => p.rangeId == node.id)

    // Store original point data for events
    node._point = point
    node._range = {
      id: point.rangeId,
      title: node.title,
      parentId: node.parentId
    }

    if ('parentId' in node) {
      let parent = lookup[node.parentId]
      ;(parent.children || (parent.children = [])).push(node)
    } else {
      root.children.push(node)
    }
  })

  Object.values(lookup).forEach(node => {
    if (node.children) {
      return
    }
    let point = serie.points.find(p => p.rangeId == node.id)
    if (point) {
      node.value = point.raw
    }
  })

  return root
}

export const preAnimate = function (chart, selection, series) {
  let serie = series.selection().datum()
    , dimensions = chart.datum().dimensions
    , showRoot = chart.datum().axes.infographic.showRoot
    , padding = showRoot ? 0 : outerPadding
    , width = dimensions.width + padding * 2
    , height = dimensions.height + padding * 2
    , previousDepth = selection.node().__depth
    , structure = hierarchy.hierarchy(buildHierarchy(chart.layout.breakdown, serie))
                           .sum(d => d.value)
                           .sort((a, b) => b.value - a.value)
    , depth = structure.height
    , wrapperPadding = 0
    , rootOffset = 0

  if (showRoot) {
    // We need extra padding on the top of a wrapper node to show back buttons
    wrapperPadding = 10
    height -= wrapperPadding
  }

  hierarchy.treemap()
           .size([width, height])
           .paddingOuter(outerPadding)
           .paddingInner(1)
           .round(true)
           (structure)

  // Group blocks that have children
  let blocks = Array.from(array.group(structure, d => d.height))
                    .map(([key, value]) => value).flat() // flatten, we don't need nesting

  // sort the blocks by the depth, so we draw the top nodes first.
  blocks.sort((a, b) => a.depth - b.depth)

  if (serie.level === undefined && showRoot) {
    // Zoomed all the way out and showing wrapper
    blocks[0].data.level = -1 // Assign this so colorscale works
  }

  // Store so we know previous depth for animations
  selection.node().__depth = depth

  selection.call(back, serie.backNode)

  if (selection.selectAll('.back-button-container').size()) {
    let bb = selection.selectAll('.back-button-container :first-child')
                      .node()
                      .getBoundingClientRect()
    rootOffset = bb.width
  }

  // returns data that should be set on each animation
  return {
    dimensions: {
      padding,
      margin: dimensions.margin,
      width,
      outerPadding,
      height,
      wrapperPadding,
      rootOffset// Changes if we show a backbutton on the root wrapper
    },
    depth,
    previousDepth,
    blocks,
    showRoot
  }

}
